import { memo, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { StockMultiSelectField } from "./StockMultiSelectField";
import { GownItemsEditor } from "./GownItemsEditor";
import { TagMultiSelectField } from "./TagMultiSelectField";
import {
  datetimeLocalToIso,
  isoToDatetimeLocal,
} from "@/shared/lib/date-format";
import type {
  RentalResponseDTO,
  RentalUpdateRequestDTO,
} from "../types/rental.types";

const rentalEditSchema = z
  .object({
    rentalType: z.enum(["SUIT", "GOWN"]),
    stockIds: z.array(z.number().int().positive()),
    gownItems: z.array(
      z.object({
        productId: z.number().int().positive(),
        quantity: z.number().int().min(1),
      }),
    ),
    expectedOutAt: z.string().min(1, "Data de saída prevista é obrigatória"),
    expectedInAt: z.string().min(1, "Data de retorno prevista é obrigatória"),
    tagIds: z.array(z.number().int().positive()),
    totalValue: z
      .number({ message: "Valor total é obrigatório" })
      .min(0, "Valor não pode ser negativo"),
    hasShoes: z.boolean(),
    shoesPairQuantity: z
      .number({ message: "Quantidade é obrigatória" })
      .int()
      .min(0, "Quantidade não pode ser negativa"),
  })
  .superRefine((data, ctx) => {
    if (data.rentalType === "SUIT" && data.stockIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["stockIds"],
        message: "Selecione ao menos uma peça",
      });
    }
    if (data.rentalType === "GOWN" && data.gownItems.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["gownItems"],
        message: "Adicione ao menos um produto beca",
      });
    }
    if (
      data.expectedInAt &&
      data.expectedOutAt &&
      data.expectedInAt <= data.expectedOutAt
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["expectedInAt"],
        message: "Data de retorno deve ser depois da saída",
      });
    }
    if (data.hasShoes && data.shoesPairQuantity <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["shoesPairQuantity"],
        message: "Informe a quantidade de pares",
      });
    }
  });

export type RentalEditFormData = z.infer<typeof rentalEditSchema>;

interface RentalEditDialogProps {
  open: boolean;
  loading: boolean;
  rental: RentalResponseDTO | null;
  onClose: () => void;
  onSubmit: (data: RentalUpdateRequestDTO) => void;
}

function buildDefaults(rental: RentalResponseDTO): RentalEditFormData {
  return {
    rentalType: rental.rentalType,
    stockIds:
      rental.stockIds ?? (rental.stockItems ?? []).map((item) => item.id),
    gownItems: (rental.gownItems ?? []).map((item) => ({
      productId: item.productId,
      quantity: item.quantity,
    })),
    expectedOutAt: isoToDatetimeLocal(rental.expectedOutAt),
    expectedInAt: isoToDatetimeLocal(rental.expectedInAt),
    tagIds: rental.tagIds,
    totalValue: rental.totalValue,
    hasShoes: rental.hasShoes,
    shoesPairQuantity: rental.shoesPairQuantity,
  };
}

export const RentalEditDialog = memo(function RentalEditDialog({
  open,
  loading,
  rental,
  onClose,
  onSubmit,
}: RentalEditDialogProps) {
  const form = useForm<RentalEditFormData>({
    resolver: zodResolver(rentalEditSchema),
    defaultValues: rental
      ? buildDefaults(rental)
      : {
          rentalType: "SUIT",
          stockIds: [],
          gownItems: [],
          expectedOutAt: "",
          expectedInAt: "",
          tagIds: [],
          totalValue: 0,
          hasShoes: false,
          shoesPairQuantity: 0,
        },
  });

  useEffect(() => {
    if (!open || !rental) return;
    form.reset(buildDefaults(rental));
  }, [open, rental, form]);

  const initialStockItems = useMemo(
    () =>
      (rental?.stockItems ?? []).map((s) => ({
        id: s.id,
        barcode: s.barcode,
        photoUrl: s.photoUrl ?? "",
      })),
    [rental],
  );

  const initialGownProducts = useMemo(
    () =>
      (rental?.gownItems ?? []).map((item) => ({
        id: item.productId,
        brand: "",
        color: "",
        name: item.productName,
      })),
    [rental],
  );

  const handleValid = (data: RentalEditFormData) => {
    if (!rental) return;
    const payload: RentalUpdateRequestDTO = {
      sellerId: rental.sellerId,
      contractId: rental.contractId,
      rentalType: rental.rentalType,
      hasShoes: data.hasShoes,
      shoesPairQuantity: data.hasShoes ? data.shoesPairQuantity : 0,
      totalValue: data.totalValue,
      payments: rental.payments ?? [],
      description: rental.description,
      status: rental.status,
      outAt: rental.outAt,
      inAt: rental.inAt,
      expectedOutAt: datetimeLocalToIso(data.expectedOutAt),
      expectedInAt: datetimeLocalToIso(data.expectedInAt),
      rentedAt: rental.rentedAt,
      stockIds: data.rentalType === "SUIT" ? data.stockIds : [],
      gownItems: data.rentalType === "GOWN" ? data.gownItems : [],
      tagIds: data.tagIds,
    };
    onSubmit(payload);
  };

  const rentalType = form.watch("rentalType");
  const hasShoes = form.watch("hasShoes");

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Aluguel</DialogTitle>
          <DialogDescription>
            {rental
              ? `Aluguel #${rental.id} · ${rental.clientName}`
              : "Aluguel"}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleValid)} className="space-y-4">
            {rentalType === "SUIT" ? (
              <FormField
                control={form.control}
                name="stockIds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Peças do estoque *</FormLabel>
                    <FormControl>
                      <StockMultiSelectField
                        inputId="rental-edit-stocks"
                        value={field.value}
                        onChange={field.onChange}
                        initialItems={initialStockItems}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="gownItems"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Produtos beca *</FormLabel>
                    <FormControl>
                      <GownItemsEditor
                        value={field.value}
                        onChange={field.onChange}
                        initialProducts={initialGownProducts}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="tagIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tags</FormLabel>
                  <FormControl>
                    <TagMultiSelectField
                      inputId="rental-edit-tags"
                      value={field.value}
                      onChange={field.onChange}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="expectedOutAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Saída prevista *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expectedInAt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Retorno previsto *</FormLabel>
                    <FormControl>
                      <Input
                        type="datetime-local"
                        {...field}
                        disabled={loading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="totalValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor total (R$) *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={field.value || ""}
                      placeholder="0,00"
                      onChange={(e) => {
                        const raw = e.target.value;
                        field.onChange(raw === "" ? 0 : Number(raw));
                      }}
                      disabled={loading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {rentalType === "SUIT" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="hasShoes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Inclui sapatos?</FormLabel>
                      <FormControl>
                        <label className="flex items-center gap-2 text-sm cursor-pointer h-10">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              if (!e.target.checked) {
                                form.setValue("shoesPairQuantity", 0);
                              }
                            }}
                            disabled={loading}
                          />
                          {field.value ? "Sim" : "Não"}
                        </label>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {hasShoes && (
                  <FormField
                    control={form.control}
                    name="shoesPairQuantity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pares de sapato *</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            value={field.value || ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              field.onChange(raw === "" ? 0 : Number(raw));
                            }}
                            disabled={loading}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Salvando..." : "Salvar Alterações"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
});
